import { takeLatest, takeEvery, put, call } from 'redux-saga/effects'
import { studentCourseConstants, snackbarActions } from '../actions'
import { studentCourseService } from '../services'

// ─── Set L1 Language for Course Group ──────────────────────────
export function* setCourseGroupL1(action) {
    const { courseGroupId, l1Id } = action.payload
    try {
        const response = yield call(
            studentCourseService.setCourseGroupL1,
            courseGroupId,
            l1Id
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.SUCCESS, data: response.data })

        // Refetch course group details and progression with the new L1
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.REQUEST,
            payload: { courseGroupId, l1Id },
        })
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.REQUEST,
            payload: { courseGroupId, l1Id },
        })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not set language for the course group.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Course Group Overview ─────────────────────────────────
export function* getCourseGroupOverview(action) {
    try {
        const response = yield call(
            studentCourseService.getCourseGroupOverview,
            action.payload.deviceId
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.SUCCESS, data: response.data })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load course groups.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Course Group By ID ────────────────────────────────────
// Uses takeEvery so multiple course groups can be fetched in parallel
export function* getCourseGroupById(action) {
    try {
        const response = yield call(
            studentCourseService.getCourseGroupById,
            action.payload.courseGroupId,
            action.payload.l1Id
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.SUCCESS, data: response.data })
    } catch (e) {
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.FAILURE,
            courseGroupId: action.payload.courseGroupId,
        })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load course group details.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Course Group Progression ──────────────────────────────
// Uses takeEvery so multiple progressions can be fetched in parallel
export function* getCourseGroupProgression(action) {
    try {
        const response = yield call(
            studentCourseService.getCourseGroupProgression,
            action.payload.courseGroupId,
            action.payload.l1Id
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.SUCCESS, data: response.data })
    } catch (e) {
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.FAILURE,
            courseGroupId: action.payload.courseGroupId,
        })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load course group progression.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Course Lesson Order ───────────────────────────────────
export function* getCourseLessonOrder(action) {
    try {
        const response = yield call(
            studentCourseService.getCourseLessonOrder,
            action.payload.courseGroupId,
            action.payload.l1Id,
            action.payload.topicId
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.SUCCESS, data: response.data })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load lesson order for this topic.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Course Lesson Order Progression ───────────────────────
export function* getCourseLessonOrderProgression(action) {
    try {
        const response = yield call(
            studentCourseService.getCourseLessonOrderProgression,
            action.payload.courseGroupId,
            action.payload.l1Id,
            action.payload.topicId
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.SUCCESS, data: response.data })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load lesson progression for this topic.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Get Lesson (Scenario V1) ──────────────────────────────────
export function* getLesson(action) {
    try {
        const response = yield call(
            studentCourseService.getLesson,
            action.payload.courseGroupId,
            action.payload.l1Id,
            action.payload.topicId,
            action.payload.customLessonId,
            action.payload.generationIndex
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON.SUCCESS, data: response.data })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_GET_LESSON.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not load the lesson.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Report Lesson (Scenario V3) ───────────────────────────────
export function* reportLesson(action) {
    try {
        const response = yield call(
            studentCourseService.reportLesson,
            action.payload.courseGroupId,
            action.payload.l1Id,
            action.payload.topicId,
            action.payload.customLessonId,
            action.payload.scenarioReport
        )
        yield put({ type: studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.SUCCESS, data: response.data })

        // Refetch progression so the UI reflects the newly earned stars
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.REQUEST,
            payload: { courseGroupId: action.payload.courseGroupId, l1Id: action.payload.l1Id },
        })
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.REQUEST,
            payload: {
                courseGroupId: action.payload.courseGroupId,
                l1Id: action.payload.l1Id,
                topicId: action.payload.topicId,
            },
        })
    } catch (e) {
        yield put({ type: studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.FAILURE })
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Could not submit lesson report.',
                options: { variant: 'warning' },
            })
        )
    }
}

// ─── Fetch Image (Student Endpoint) ────────────────────────────
// Uses takeEvery so multiple images can be fetched in parallel
export function* fetchImage(action) {
    try {
        const response = yield call(studentCourseService.fetchImage, action.payload.imageUrl)
        const blobUrl = URL.createObjectURL(response.data)
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.SUCCESS,
            imageUrl: action.payload.imageUrl,
            blobUrl: blobUrl,
        })
    } catch (e) {
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.FAILURE,
            imageUrl: action.payload.imageUrl,
        })
    }
}

// ─── Fetch Audio (Student Endpoint) ─────────────────────────────
// Uses takeEvery so multiple audio files can be fetched in parallel
export function* fetchAudio(action) {
    try {
        const response = yield call(studentCourseService.fetchAudio, action.payload.audioUrl)
        const blobUrl = URL.createObjectURL(response.data)
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.SUCCESS,
            audioUrl: action.payload.audioUrl,
            blobUrl: blobUrl,
        })
    } catch (e) {
        yield put({
            type: studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.FAILURE,
            audioUrl: action.payload.audioUrl,
        })
    }
}

// ─── Watcher ───────────────────────────────────────────────────
export function* studentCourseSagas() {
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.REQUEST, setCourseGroupL1)
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.REQUEST, getCourseGroupOverview)
    // takeEvery: allows parallel fetches for multiple course groups and images
    yield takeEvery(studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.REQUEST, getCourseGroupById)
    yield takeEvery(studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.REQUEST, getCourseGroupProgression)
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.REQUEST, getCourseLessonOrder)
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.REQUEST, getCourseLessonOrderProgression)
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_GET_LESSON.REQUEST, getLesson)
    yield takeLatest(studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.REQUEST, reportLesson)
    yield takeEvery(studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.REQUEST, fetchImage)
    yield takeEvery(studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.REQUEST, fetchAudio)
}
